import React, { useState, useEffect } from 'react';
import { Plus, Filter, Search, Package2, Pencil, Trash2, GripVertical } from 'lucide-react';
import EmptyState from '../../components/EmptyState';
import MaterialModal from '../../components/MaterialModal';
import DeleteConfirmationModal from '../../components/DeleteConfirmationModal';
import { getMaterials, deleteMaterial, Material, MaterialCategory, getMaterialCategories } from '../../lib/materials';
import { useBusiness } from '../../contexts/BusinessContext';
import { ChevronDown } from 'lucide-react';

export default function Materials() {
  const { selectedBusiness } = useBusiness();
  const [items, setItems] = useState<Material[]>([]);
  const [categories, setCategories] = useState<MaterialCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState<Material | null>(null);
  const [modalMode, setModalMode] = useState<'create' | 'edit'>('create');
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [materialToDelete, setMaterialToDelete] = useState<Material | null>(null);
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set());
  const [isMobile, setIsMobile] = useState(false);

  // Check if mobile viewport
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 640); // sm breakpoint
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const loadItems = async () => {
    if (!selectedBusiness?.id) return;

    try {
      setLoading(true);
      const [data, categoriesData] = await Promise.all([
        getMaterials(selectedBusiness.id),
        getMaterialCategories(selectedBusiness.id)
      ]);
      setItems(data);
      setCategories(categoriesData);
      setError(null);
      
      // Auto-expand all categories on mobile for better UX
      if (isMobile) {
        const categoryIds = data.reduce((acc, item) => {
          const categoryId = item.category_id || 'uncategorized';
          acc.add(categoryId);
          return acc;
        }, new Set<string>());
        setExpandedCategories(categoryIds);
      }
    } catch (err) {
      console.error('Error loading materials:', err);
      setError(err instanceof Error ? err.message : 'Failed to load materials');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadItems();
  }, [selectedBusiness?.id, isMobile]);

  const handleAddItem = () => {
    setSelectedItem(null);
    setModalMode('create');
    setIsModalOpen(true);
  };

  const handleEditItem = (item: Material) => {
    setSelectedItem(item);
    setModalMode('edit');
    setIsModalOpen(true);
  };

  const handleDelete = (material: Material) => {
    setMaterialToDelete(material);
    setIsDeleteModalOpen(true);
  };

  const confirmDelete = async () => {
    if (!materialToDelete) return;
    
    try {
      setDeleteLoading(true);
      await deleteMaterial(materialToDelete.id);
      await loadItems();
      setIsDeleteModalOpen(false);
      setMaterialToDelete(null);
    } catch (err) {
      console.error('Error deleting material:', err);
      setError(err instanceof Error ? err.message : 'Failed to delete material');
    } finally {
      setDeleteLoading(false);
    }
  };

  const toggleCategory = (categoryId: string) => {
    // On mobile, don't collapse categories for better UX
    if (isMobile) return;
    
    setExpandedCategories(prev => {
      const next = new Set(prev);
      if (next.has(categoryId)) {
        next.delete(categoryId);
      } else {
        next.add(categoryId);
      }
      return next;
    });
  };

  const toggleAllCategories = () => {
    if (expandedCategories.size === itemsByCategory.length) {
      // Collapse all
      setExpandedCategories(new Set());
    } else {
      // Expand all
      const allIds = itemsByCategory.map(cat => cat.id);
      setExpandedCategories(new Set(allIds));
    }
  };

  // Group items by category
  const itemsByCategory = React.useMemo(() => {
    const grouped = items.reduce((acc, item) => {
      const categoryId = item.category_id || 'uncategorized';
      if (!acc[categoryId]) {
        acc[categoryId] = {
          id: categoryId,
          name: item.category?.name || 'Uncategorized',
          items: []
        };
      }
      acc[categoryId].items.push(item);
      return acc;
    }, {} as Record<string, { id: string; name: string; items: Material[] }>);

    return Object.values(grouped).sort((a, b) => a.name.localeCompare(b.name));
  }, [items]);

  const filteredItems = items.filter(item => {
    const searchLower = searchQuery.toLowerCase();
    return (
      item.name.toLowerCase().includes(searchLower) ||
      item.description?.toLowerCase().includes(searchLower)
    );
  });

  return (
    <div 
      className="min-h-screen bg-gray-100 p-2 sm:p-4"
      style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif' }}
    >
      <div className="max-w-6xl mx-auto space-y-4 sm:space-y-6 pb-24">
        <div className="bg-gray-50 rounded-2xl shadow-lg border border-gray-200/50 overflow-hidden">
          {/* Mobile-optimized search section */}
          <div className="px-4 sm:px-8 py-4 sm:py-6 border-b border-gray-200/50">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-3 sm:space-y-0">
              <div className="relative flex-1 sm:max-w-md">
                <Search className="absolute left-3 sm:left-4 top-1/2 -translate-y-1/2 h-4 w-4 sm:h-5 sm:w-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search materials..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 sm:pl-12 pr-4 py-2.5 sm:py-3 text-sm sm:text-base bg-white border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-all duration-200"
                />
              </div>
              <div className="flex items-center space-x-3">
                <button 
                  onClick={handleAddItem}
                  className="w-full sm:w-auto inline-flex items-center justify-center px-4 py-2.5 sm:py-2 text-sm font-semibold text-white bg-purple-700 rounded-xl hover:bg-purple-800 transition-all duration-200 active:scale-95 shadow-sm"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add Material
                </button>
                {/* Desktop expand/collapse all button */}
                {!isMobile && itemsByCategory.length > 1 && (
                  <button
                    onClick={toggleAllCategories}
                    className="text-sm text-purple-600 hover:text-purple-700 font-medium px-3 py-2 rounded-lg hover:bg-purple-50 transition-colors duration-200"
                  >
                    {expandedCategories.size === itemsByCategory.length ? 'Collapse All' : 'Expand All'}
                  </button>
                )}
              </div>
            </div>
          </div>

          {loading ? (
            <div className="flex items-center justify-center h-48 sm:h-64">
              <div className="text-center">
                <div className="animate-spin rounded-full h-10 w-10 sm:h-12 sm:w-12 border-b-2 border-purple-600 mx-auto"></div>
                <p className="mt-3 sm:mt-4 text-sm sm:text-base text-gray-600">Loading materials...</p>
              </div>
            </div>
          ) : error ? (
            <div className="p-4 sm:p-8">
              <div className="bg-red-50 border border-red-200 text-red-600 px-4 sm:px-6 py-3 sm:py-4 rounded-xl text-sm sm:text-base">
                {error}
              </div>
            </div>
          ) : filteredItems.length === 0 ? (
            <div className="flex items-center justify-center h-48 sm:h-64">
              <div className="text-center py-8 sm:py-12 px-4">
                <Package2 className="h-10 w-10 sm:h-12 sm:w-12 text-gray-400 mx-auto mb-3 sm:mb-4" />
                <p className="text-base sm:text-lg font-semibold text-gray-900">No materials</p>
                <p className="text-sm sm:text-base text-gray-500 mt-1 sm:mt-2">
                  Start adding materials to track costs and inventory.
                </p>
                <button
                  onClick={handleAddItem}
                  className="mt-4 sm:mt-6 w-full sm:w-auto inline-flex items-center justify-center px-6 py-3 text-sm sm:text-base font-semibold text-white bg-purple-700 hover:bg-purple-800 rounded-xl transition-all duration-200 active:scale-95"
                >
                  <Plus className="h-4 w-4 sm:h-5 sm:w-5 mr-2" />
                  Add your first material
                </button>
              </div>
            </div>
          ) : (
            <div className="p-4 sm:p-8 space-y-4 sm:space-y-6">
              {itemsByCategory.map((category) => (
                <div key={category.id} className="bg-white rounded-2xl border border-gray-200/50 shadow-sm overflow-hidden">
                  {/* Enhanced category header */}
                  <div 
                    className={`px-4 sm:px-6 py-4 sm:py-4 bg-gray-50 border-b border-gray-200/50 flex items-center justify-between transition-all duration-200 ${
                      isMobile ? '' : 'cursor-pointer hover:bg-gray-100 active:bg-gray-200'
                    } touch-manipulation`}
                    onClick={() => toggleCategory(category.id)}
                  >
                    <div className="flex items-center space-x-3 sm:space-x-3">
                      <ChevronDown 
                        className={`h-5 w-5 sm:h-5 sm:w-5 text-gray-400 transition-all duration-300 ease-out ${
                          expandedCategories.has(category.id) ? 'rotate-180 text-purple-500' : ''
                        } ${isMobile ? 'opacity-50' : ''}`}
                      />
                      <h3 className="text-base sm:text-base font-semibold text-gray-900">{category.name}</h3>
                      <span className="text-xs sm:text-sm text-gray-500 bg-gray-200 px-2.5 py-1 rounded-full font-medium min-w-[24px] text-center">
                        {category.items.length}
                      </span>
                    </div>
                    {!isMobile && (
                      <div className="text-xs text-gray-400 font-medium">
                        {expandedCategories.has(category.id) ? 'Click to collapse' : 'Click to expand'}
                      </div>
                    )}
                  </div>
                  
                  {/* Enhanced collapsible content */}
                  <div className={`transition-all duration-300 ease-out ${
                    expandedCategories.has(category.id) 
                      ? 'max-h-[2000px] opacity-100' 
                      : 'max-h-0 opacity-0 overflow-hidden'
                  }`}>
                    <div className="divide-y divide-gray-200/50">
                      {category.items.map((item) => (
                        <div key={item.id} className="p-4 sm:p-6 hover:bg-gray-50 transition-colors duration-200">
                          {/* Mobile-optimized material item layout */}
                          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between space-y-3 sm:space-y-0">
                            <div className="flex items-start space-x-2 sm:space-x-4 flex-1 min-w-0">
                              {/* Hide grip handle on mobile */}
                              <button className="hidden sm:block cursor-grab active:cursor-grabbing p-2 -ml-2 text-gray-400 hover:text-gray-600 rounded-xl hover:bg-gray-100 transition-all duration-200">
                                <GripVertical className="h-5 w-5" />
                              </button>
                              <div className="flex-1 min-w-0">
                                <h3 className="text-sm sm:text-base font-semibold text-gray-900 truncate">{item.name}</h3>
                                {item.description && (
                                  <p className="mt-1 text-sm sm:text-base text-gray-600 line-clamp-2">{item.description}</p>
                                )}
                                <div className="mt-2 sm:mt-3 text-xs sm:text-sm font-medium text-gray-500">
                                  ${item.cost.toFixed(2)} per {item.unit}
                                </div>
                              </div>
                            </div>
                            {/* Mobile-optimized action buttons */}
                            <div className="flex items-center justify-end space-x-2 sm:space-x-2">
                              <button
                                onClick={() => handleEditItem(item)}
                                className="p-2.5 sm:p-2 text-gray-400 hover:text-gray-600 rounded-xl hover:bg-gray-100 transition-all duration-200 active:scale-95 touch-manipulation"
                              >
                                <Pencil className="h-4 w-4 sm:h-5 sm:w-5" />
                              </button>
                              <button
                                onClick={() => handleDelete(item)}
                                className="p-2.5 sm:p-2 text-red-400 hover:text-red-600 rounded-xl hover:bg-red-50 transition-all duration-200 active:scale-95 touch-manipulation"
                              >
                                <Trash2 className="h-4 w-4 sm:h-5 sm:w-5" />
                              </button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          <MaterialModal
            isOpen={isModalOpen}
            onClose={() => {
              setIsModalOpen(false);
              setSelectedItem(null);
            }}
            onSuccess={() => {
              setIsModalOpen(false);
              setSelectedItem(null);
              loadItems();
            }}
            businessId={selectedBusiness?.id}
            item={selectedItem}
            mode={modalMode}
          />
        </div>

        <DeleteConfirmationModal
          isOpen={isDeleteModalOpen}
          onClose={() => {
            setIsDeleteModalOpen(false);
            setMaterialToDelete(null);
          }}
          onConfirm={confirmDelete}
          title="Delete Material"
          message={`Are you sure you want to delete "${materialToDelete?.name}"? This action cannot be undone.`}
          loading={deleteLoading}
        />
      </div>
    </div>
  );
}